import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Target, Users, School, ChefHat, MapPin, Building2, Compass, Handshake, HeartHandshake, Star, Bell, Shield, Network, Layout, CheckCircle2, TrendingUp, X,
  CreditCard, Lock, FileText, Link, Eye, Clock, Award, Zap, Mail, Gift, Heart, Scale, RefreshCw, MessageSquare, BarChart3, AlertTriangle, Download, Settings, Landmark,
  Lightbulb, Monitor, Laptop, Smartphone, ClipboardList, PieChart, Baby, Utensils
} from 'lucide-react';

// All English Sections for EcoLuncH Media Kit

export const VisionSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1546410531-bb4caa6b424d" 
        alt="Vision" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-emerald-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <Target className="w-20 h-20 mx-auto mb-4" />
          <h2 className="text-4xl font-bold mb-4">Vision & Objectives</h2>
          <p className="text-xl">A digital infrastructure for school meal ecosystem</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Target className="w-6 h-6 text-green-600" />
          <span>Global Mission</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-lg text-gray-700">
          EcoLuncH is a <strong className="text-green-600">revolutionary digital infrastructure</strong> dedicated to durably transforming the management,
          distribution and monitoring of school meal quality <strong className="text-green-700">across all of Quebec</strong>.
        </p>
        <p className="text-lg text-gray-700">
          It aims to <strong className="text-emerald-600">centralize, automate and simplify</strong> the entire sector by bringing together, within a single environment, parents, schools, caterers and school service centers (SSC). By creating a common, standardized, fluid, transparent and measurable framework, EcoLuncH positions school meals in a uniform logic across Quebec and <strong className="text-green-600">establishes itself as the market's reference infrastructure</strong>.
        </p>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg">
          <h3 className="text-xl font-bold text-green-700 mb-4">Multi-Level Model</h3>
          <p className="text-gray-700 mb-4">
            EcoLuncH acts as a <strong>school food governance platform</strong> based on a multi-level model:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <span>Parents/Children: order and provide feedback</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <span>Caterers: plan production and manage operations</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <span>Schools: monitor local quality and volumes</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <span>School Directions: supervise one or more schools / establishments</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <span>SSC: govern and audit the entire system</span>
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl">Key Principles</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <ul className="space-y-4">
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Autonomy + Interconnection</strong>
                <p className="text-sm text-gray-600">Each portal is specialized but connected</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Automation of processes</strong>
                <p className="text-sm text-gray-600">Minimize manual data entry</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Real-time feedback</strong>
                <p className="text-sm text-gray-600">Child evaluations inform the system immediately</p>
              </div>
            </li>
          </ul>
          <ul className="space-y-4">
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Continuous improvement</strong>
                <p className="text-sm text-gray-600">Data for better menus and quality</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Precise role control (RBAC)</strong>
                <p className="text-sm text-gray-600">Rights assigned according to function</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Absolute security</strong>
                <p className="text-sm text-gray-600">Data compartmentalized by entity</p>
              </div>
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-br from-green-50 to-emerald-50">
      <CardHeader>
        <CardTitle className="text-xl">Objectives by Actor</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-green-200">
                <th className="text-left p-3 font-semibold">Actor</th>
                <th className="text-left p-3 font-semibold">Main Objective</th>
                <th className="text-left p-3 font-semibold">Added Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-green-100">
              <tr>
                <td className="p-3"><Users className="w-4 h-4 inline text-cyan-400" /> Parents</td>
                <td className="p-3">Order quickly</td>
                <td className="p-3 text-green-600 font-medium">Time saving, visibility</td>
              </tr>
              <tr>
                <td className="p-3"><ChefHat className="w-4 h-4 inline text-orange-400" /> Caterers</td>
                <td className="p-3">Automate management</td>
                <td className="p-3 text-green-600 font-medium">Operational efficiency</td>
              </tr>
              <tr>
                <td className="p-3"><School className="w-4 h-4 inline text-green-400" /> Schools</td>
                <td className="p-3">Monitor satisfaction</td>
                <td className="p-3 text-green-600 font-medium">Management tool</td>
              </tr>
              <tr>
                <td className="p-3"><Building2 className="w-4 h-4 inline text-teal-400" /> School Directions</td>
                <td className="p-3">Supervise one or more schools</td>
                <td className="p-3 text-green-600 font-medium">Consolidated view</td>
              </tr>
              <tr>
                <td className="p-3"><Landmark className="w-4 h-4 inline text-red-400" /> SSC</td>
                <td className="p-3">Govern and audit</td>
                <td className="p-3 text-green-600 font-medium">Strategic vision</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardHeader>
        <CardTitle className="text-xl">Usage Example</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <p className="text-gray-700">A parent orders for two children in <strong className="text-purple-600">15 seconds</strong> via mobile app</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <p className="text-gray-700">The caterer receives the order <strong className="text-orange-600">automatically</strong> integrated into their production sheet</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <p className="text-gray-700">The school sees the adjusted volume in their dashboard <strong className="text-indigo-600">in real-time</strong></p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">4</div>
            <p className="text-gray-700">The SSC notes a participation increase of <strong className="text-red-600">xx%</strong> in the region</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const ArchitectureSectionEN = () => {
  const [selectedPortal, setSelectedPortal] = useState(null);
  
  return (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644" alt="Architecture" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-cyan-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Global Architecture</h2>
          <p className="text-xl">5 interconnected portals, 1 central back-end</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            <Layout className="w-7 h-7 text-white" />
          </div>
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-bold">Multi-Portal Architecture</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-lg text-gray-700">
          EcoLuncH is based on an <strong className="text-blue-600">interconnected multi-portal architecture</strong> with an intelligent central back-end.
        </p>

        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 p-8 rounded-2xl border-2 border-blue-400 shadow-2xl relative overflow-hidden">
          {/* Fox mosaic pattern in architecture blue */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url('/fox-pattern-transparent.png')`,
              backgroundRepeat: 'repeat',
              backgroundSize: '70px 70px',
              opacity: 0.12
            }}
          />
          <div className="text-center space-y-6">
            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('css')}
            >
              <div className="bg-gradient-to-br from-red-500 to-red-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-red-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-red-400 hover:border-red-300">
                <Landmark className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">SSC (Board)</div>
                <div className="text-sm">Governance & Policy</div>
                <div className="text-xs mt-2 opacity-70 italic">click to flip</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-red-400 via-pink-400 to-teal-500 shadow-lg shadow-pink-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('directions')}
            >
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-teal-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-teal-400 hover:border-teal-300">
                <Building2 className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">School Directions</div>
                <div className="text-sm">Multi-school supervision</div>
                <div className="text-xs mt-2 opacity-70 italic">click to flip</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-teal-400 via-blue-400 to-indigo-500 shadow-lg shadow-indigo-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('ecoles')}
            >
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-indigo-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-indigo-400 hover:border-indigo-300">
                <School className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Local Schools</div>
                <div className="text-sm">Monitoring cockpit & communication</div>
                <div className="text-xs mt-2 opacity-70 italic">click to flip</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-indigo-400 via-amber-400 to-orange-500 shadow-lg shadow-orange-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('traiteurs')}
            >
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-orange-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-orange-400 hover:border-orange-300">
                <ChefHat className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Caterers</div>
                <div className="text-sm">Production back-office</div>
                <div className="text-xs mt-2 opacity-70 italic">click to flip</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-orange-400 via-pink-400 to-purple-500 shadow-lg shadow-purple-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('parents')}
            >
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-purple-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-purple-400 hover:border-purple-300">
                <Users className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Parents / Children</div>
                <div className="text-sm">Orders, payments, feedback</div>
                <div className="text-xs mt-2 opacity-70 italic">click to flip</div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="w-1 h-16 bg-gradient-to-b from-purple-400 via-cyan-400 to-cyan-500 shadow-lg shadow-cyan-500/50"></div>
            </div>

            <div 
              className="inline-block cursor-pointer"
              onClick={() => setSelectedPortal('coparentalite')}
            >
              <div className="bg-gradient-to-br from-cyan-500 to-indigo-600 text-white px-8 py-4 rounded-xl shadow-lg transform hover:shadow-cyan-500/50 hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-cyan-400 hover:border-cyan-300">
                <HeartHandshake className="w-10 h-10 mx-auto mb-2" />
                <div className="font-bold text-xl">Co-Parenting</div>
                <div className="text-sm">Collaborative management module</div>
                <div className="text-xs mt-2 opacity-70 italic">click to flip</div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-gradient-to-r from-slate-800 to-slate-700 p-4 rounded-xl shadow-lg border border-blue-400/30">
            <p className="text-center text-white font-medium">
              <strong className="text-cyan-400"><Lightbulb className="w-4 h-4 inline text-yellow-400" /> Click on each portal</strong>
              <span className="block text-sm mt-1 text-gray-300">to discover its frontend interface details</span>
            </p>
          </div>
          
          <div className="mt-6 bg-white/10 backdrop-blur-sm p-4 rounded-xl shadow-md border border-blue-300/50">
            <p className="text-center text-white font-medium text-sm">
              <strong className="text-blue-300">Key features:</strong>
              <span className="inline-block bg-blue-500/30 px-3 py-1 rounded-full text-xs mx-1 border border-blue-400">Real-time</span>
              <span className="inline-block bg-indigo-500/30 px-3 py-1 rounded-full text-xs mx-1 border border-indigo-400">Secure</span>
              <span className="inline-block bg-purple-500/30 px-3 py-1 rounded-full text-xs mx-1 border border-purple-400">Synchronized</span>
            </p>
          </div>
        </div>

        {/* Modal Overlay for Portal Details */}
        {selectedPortal && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPortal(null)}
          >
            <div 
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl max-w-lg w-full border-2 relative"
              style={{
                borderColor: selectedPortal === 'css' ? '#ef4444' :
                            selectedPortal === 'directions' ? '#14b8a6' :
                            selectedPortal === 'ecoles' ? '#6366f1' :
                            selectedPortal === 'traiteurs' ? '#f97316' :
                            selectedPortal === 'parents' ? '#a855f7' :
                            '#06b6d4'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Section Indicator Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white text-xs font-bold shadow-lg">
                PORTAL CLICKED
              </div>

              {/* Close Button */}
              <button 
                onClick={() => setSelectedPortal(null)}
                className="absolute top-3 right-3 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="p-5">
                {selectedPortal === 'css' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                        <Landmark className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">SSC (Board)</h3>
                        <p className="text-sm text-red-300">Governance & Policy</p>
                      </div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Real-time executive dashboard</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Multi-school & direction management</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Consolidated reports & KPIs</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Policy configuration</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span>Analytics & Business Intelligence</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'directions' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                        <Building2 className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">School Directions</h3>
                        <p className="text-sm text-teal-300">Multi-school supervision</p>
                      </div>
                    </div>
                    <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Unified multi-school view</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Regional supervision dashboard</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>School principal management</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Inter-school comparative reports</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span>Budget & resource allocation</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'ecoles' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <School className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Local Schools</h3>
                        <p className="text-sm text-indigo-300">Cockpit, alerts</p>
                      </div>
                    </div>
                    <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Daily management cockpit</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Real-time order tracking</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Alerts & push notifications</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>Student & absence management</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <span>School activity reports</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'traiteurs' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                        <ChefHat className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Caterers</h3>
                        <p className="text-sm text-orange-300">Back-office</p>
                      </div>
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Complete production back-office</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Menu management & planning</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Order tracking & modifications</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Stock & inventory management</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span>Invoicing & payments</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'parents' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <Users className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Parents / Children</h3>
                        <p className="text-sm text-purple-300">Orders, payments, feedback</p>
                      </div>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <div className="font-bold text-purple-300 mb-3 text-sm flex items-center space-x-2">
                        <span className="flex space-x-1"><Monitor className="w-4 h-4 text-cyan-400" /><Laptop className="w-4 h-4 text-cyan-400" /><Smartphone className="w-4 h-4 text-cyan-400" /></span>
                        <span>Multi-Platform</span>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Responsive web interface</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Android/iOS mobile apps</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Parent Mode: complete management</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Child Mode: playful interface</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Quick multi-child ordering</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span>Flexible payments & wallet</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}

                {selectedPortal === 'coparentalite' && (
                  <>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <HeartHandshake className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Co-Parenting</h3>
                        <p className="text-sm text-cyan-300">Collaborative management module</p>
                      </div>
                    </div>
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                      <div className="font-bold text-cyan-300 mb-3 text-sm flex items-center space-x-2">
                        <span className="flex space-x-1"><Monitor className="w-4 h-4 text-cyan-400" /><Laptop className="w-4 h-4 text-cyan-400" /><Smartphone className="w-4 h-4 text-cyan-400" /></span>
                        <span>Parent Portal Extension</span>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-200">
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Dedicated Web + Mobile interface</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Shared dashboard for 2 parents</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Configurable granular permissions</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Automatic payment split</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Third-party payment page (no account)</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span>Complete history & audit</span>
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
  );
};

export const CatererSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img src="https://images.unsplash.com/photo-1675647699232-76b8f533b006" alt="Caterer" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-orange-600/90 to-amber-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Caterer Portal</h2>
          <p className="text-xl">Complete management back-office</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2"><ChefHat className="w-6 h-6 text-orange-600" /><span>Complete School Activity Management</span></CardTitle>
        <CardDescription>A single panel to manage all activities: orders, menus, production, payments, and reports</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><ClipboardList className="w-4 h-4 inline text-orange-400" /> Orders</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Display and sort</li><li>• Cancellations / Modifications</li><li>• Automatic credits</li></ul></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><Utensils className="w-4 h-4 inline text-orange-400" /> Menus</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Easy duplication</li><li>• Holiday management</li><li>• Variations per school</li></ul></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><Building2 className="w-4 h-4 inline text-orange-400" /> Production</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Automatic aggregation</li><li>• Real-time volumes</li><li>• Optimized planning</li></ul></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><CreditCard className="w-4 h-4 inline text-orange-400" /> Accounting</h4><ul className="text-sm text-gray-700 space-y-1"><li>• QuickBooks</li><li>• Sage</li><li>• Acomba / Acomba Go</li></ul></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><CreditCard className="w-4 h-4 inline text-orange-400" /> Payments</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Clover</li><li>• Sezzle</li></ul></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><Bell className="w-4 h-4 inline text-orange-400" /> Alerts</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Dishes below threshold</li><li>• Automatic / customized</li><li>• Push notifications</li></ul></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><BarChart3 className="w-4 h-4 inline text-orange-400" /> Reports</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Top / Flop dishes</li><li>• Participation</li><li>• Automation</li></ul></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg"><h4 className="font-bold text-orange-600 mb-2"><MessageSquare className="w-4 h-4 inline text-cyan-400" /> Communication</h4><ul className="text-sm text-gray-700 space-y-1"><li>• School ↔ caterer channel</li><li>• Integrated chat</li><li>• File sharing</li></ul></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-xl bg-gradient-to-r from-orange-50 to-amber-50">
      <CardHeader>
        <CardTitle className="text-2xl">Logic and Interactions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <div className="flex-1">
              <p className="text-gray-700">Menus created by the caterer are <strong className="text-orange-600">automatically synchronized</strong> with school portals</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <div className="flex-1">
              <p className="text-gray-700">Parent order data <strong className="text-orange-600">automatically feeds back</strong> to the caterer's production sheet</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <div className="flex-1">
              <p className="text-gray-700">Production reports are <strong className="text-orange-600">automatically prepared</strong> in the desired formats</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">4</div>
            <div className="flex-1">
              <p className="text-gray-700">In case of problem (low dish, alert), an <strong className="text-orange-600">automatic alert</strong> is triggered</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-xl bg-gradient-to-r from-gray-50 to-slate-50">
      <CardHeader>
        <CardTitle className="text-2xl">Security & Confidentiality</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white"><Lock className="w-4 h-4" /></div>
            <div>
              <div className="font-bold text-gray-800">Private bubble</div>
              <p className="text-sm text-gray-600">The caterer operates in a private environment</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white"><Eye className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">Limited visibility</div>
              <p className="text-sm text-gray-600">Schools and SSC only see aggregates (order volumes, satisfaction), not internal details (costs, profit margins, etc.)</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white"><Shield className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">Protected data</div>
              <p className="text-sm text-gray-600">Complete protection of commercial and financial data</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const ParentSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img src="https://images.unsplash.com/photo-1551650975-87deedd944c3" alt="Parent" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-purple-600/90 to-pink-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Parent / Child Portal</h2>
          <p className="text-xl">Simple, intuitive, fast and fun</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2"><Users className="w-6 h-6 text-purple-600" /><span>Simplifying Family Life</span></CardTitle>
        <CardDescription>Intuitive interface for parents and playful experience for children</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-bold text-purple-600 mb-4 flex items-center space-x-2"><span><Users className="w-4 h-4" /></span><span>Parent Mode</span></h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Computer</strong><p className="text-sm text-gray-600">Complete web interface</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Android & Apple apps</strong><p className="text-sm text-gray-600">Native mobile applications</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Multi-child quick order</strong><p className="text-sm text-gray-600">Quick Order: 1 click = all children</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Personalized colors</strong><p className="text-sm text-gray-600">For each child, simplifies management</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Weekly, monthly or annual meal customization</strong><p className="text-sm text-gray-600">Flexible short, medium and long-term planning</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Automatic subscriptions</strong><p className="text-sm text-gray-600">Recurring or one-time payment</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Flexible payments</strong><p className="text-sm text-gray-600">Wallet & payment methods</p><p className="text-sm text-gray-600">Manage cards, Sezzle and internal balance easily</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>Personalized notifications</strong><p className="text-sm text-gray-600">Reminders and confirmations</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" /><div><strong>History and receipts</strong><p className="text-sm text-gray-600">Complete order tracking</p></div></li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold text-pink-600 mb-4 flex items-center space-x-2"><span><Baby className="w-4 h-4" /></span><span>Child Mode</span></h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" /><div><strong>Interactive game</strong><p className="text-sm text-gray-600">Fun and playful ordering</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" /><div><strong>Avatar creation</strong><p className="text-sm text-gray-600">Personalized avatar for each child</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" /><div><strong>Portal management</strong><p className="text-sm text-gray-600">Child-friendly interface</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" /><div><strong>Custom themes</strong><p className="text-sm text-gray-600">Choice of themes and colors</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" /><div><strong>Badge system</strong><p className="text-sm text-gray-600">Rewards for participation</p></div></li>
              <li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-pink-600 mt-0.5 flex-shrink-0" /><div><strong>Active involvement</strong><p className="text-sm text-gray-600">Develops food autonomy</p></div></li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="text-xl">UX Logic</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Zap className="w-4 h-4" /></div>
            <p className="text-gray-700"><strong>1 click</strong> = all children ordered</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Smartphone className="w-4 h-4" /></div>
            <p className="text-gray-700"><strong>Mobile-first</strong> interface</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Bell className="w-4 h-4" /></div>
            <p className="text-gray-700">Reminders if meal <strong>not rated</strong></p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold"><Lightbulb className="w-4 h-4" /></div>
            <p className="text-gray-700">Suggestions and comments <strong>optional</strong></p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl flex items-center space-x-2">
            <Shield className="w-5 h-5 text-purple-600" />
            <span>Confidentiality</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">No global data visible</p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">No general statistics accessible</p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">Feedback <strong>anonymized and weighted</strong></p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
            <p className="text-gray-700">Complete protection of family data</p>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-r from-purple-100 to-pink-100">
      <CardContent className="py-8">
        <div className="text-center">
          <div className="inline-block bg-white px-8 py-6 rounded-2xl shadow-lg max-w-2xl">
            <p className="text-3xl font-bold text-purple-600 mb-3"><Zap className="w-5 h-5 inline text-purple-400" /> 15 seconds</p>
            <p className="text-gray-700 text-lg leading-relaxed">to order meals for all your children — each with their own menu, for the week, month or year!</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);


export const CoParentingSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl shadow-2xl">
      <div className="bg-gradient-to-r from-cyan-600 via-indigo-600 to-purple-600 py-12">
        <div className="text-center text-white p-6">
          <HeartHandshake className="w-16 h-16 mx-auto mb-4 opacity-90" />
          <h2 className="text-4xl font-bold mb-3">Co-Parenting Module</h2>
          <p className="text-xl opacity-90">Collaborative management module for modern families</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <HeartHandshake className="w-6 h-6 text-blue-600" />
          <span>Collaborative Management Module</span>
        </CardTitle>
        <CardDescription>Shared access and configurable permissions for harmonious co-parenting</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border border-blue-200">
            <h4 className="font-bold text-blue-700 mb-3 flex items-center space-x-2"><Users className="w-5 h-5" /><span>Multi-Parent Access</span></h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Invitation via unique link</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Dual approval validation</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Real-time synchronization</span></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-xl border border-purple-200">
            <h4 className="font-bold text-purple-700 mb-3 flex items-center space-x-2"><Shield className="w-5 h-5" /><span>Configurable Permissions</span></h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Who can order</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Who can view</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Who can pay</span></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-5 rounded-xl border border-orange-200">
            <h4 className="font-bold text-orange-700 mb-3 flex items-center space-x-2"><Shield className="w-5 h-5" /><span>Enhanced Security</span></h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Separate encrypted payments</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Complete activity log</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>IP and timestamp traceability</span></li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-slate-100 p-5 rounded-xl border border-gray-200">
            <h4 className="font-bold text-gray-700 mb-3 flex items-center space-x-2"><Bell className="w-5 h-5" /><span>Smart Notifications</span></h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Real-time alerts</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Interactive approvals</span></li>
              <li className="flex items-center space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /><span>Action history</span></li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2"><CreditCard className="w-6 h-6 text-orange-600" /><span>Payment Sharing</span></CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0"><BarChart3 className="w-5 h-5 text-blue-600" /></div>
          <div><p className="font-bold text-blue-700">Automatic Split</p><p className="text-sm text-gray-600">Predefined options: 50/50, 60/40, 70/30 or custom percentage</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
          <div><p className="font-bold text-green-700">Dual Approval Validation</p><p className="text-sm text-gray-600">Both parents must approve the split before activation</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-xl">
          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0"><Lock className="w-5 h-5 text-yellow-600" /></div>
          <div><p className="font-bold text-yellow-700">Separate Encrypted Payments</p><p className="text-sm text-gray-600">Each parent enters their payment method. Neither sees the other's info</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-purple-600" /></div>
          <div><p className="font-bold text-purple-700">Individual Invoices</p><p className="text-sm text-gray-600">Automatic separate receipts. Exportable annual PDF report</p></div>
        </div>
        <div className="flex items-start space-x-4 p-4 bg-cyan-50 rounded-xl">
          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0"><Link className="w-5 h-5 text-cyan-600" /></div>
          <div><p className="font-bold text-cyan-700">Unique Payment Link</p><p className="text-sm text-gray-600">Send payment link to third parties (grandparents, family, friends) for one-time contribution without registration</p></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-xl bg-gradient-to-br from-slate-800 to-gray-900 text-white">
      <CardHeader>
        <CardTitle className="text-2xl text-white">External Payment Link — <span className="text-cyan-400">Unique Feature</span></CardTitle>
        <CardDescription className="text-gray-300">Parent manages the complete order, third party pays via dedicated interface</CardDescription>
      </CardHeader>
      <CardContent>
        <h4 className="font-bold text-lg mb-4 text-gray-200">How It Works</h4>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-blue-900/40 p-4 rounded-xl border border-blue-500/30">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-3">1</div>
            <p className="font-bold text-blue-300 mb-1">Parent creates complete order</p>
            <p className="text-sm text-gray-400">Configures all meals, menus and preferences for their child(ren) via their portal</p>
          </div>
          <div className="bg-blue-900/40 p-4 rounded-xl border border-blue-500/30">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-3">2</div>
            <p className="font-bold text-blue-300 mb-1">Personalized link generation</p>
            <p className="text-sm text-gray-400">System generates a unique and secure payment link for this specific order</p>
          </div>
          <div className="bg-blue-900/40 p-4 rounded-xl border border-blue-500/30">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-3">3</div>
            <p className="font-bold text-blue-300 mb-1">Send to third-party payer</p>
            <p className="text-sm text-gray-400">Parent sends the link (email, SMS, WhatsApp) to the person who will make the payment</p>
          </div>
          <div className="bg-cyan-900/40 p-4 rounded-xl border border-cyan-500/30">
            <div className="w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold mb-3">4</div>
            <p className="font-bold text-cyan-300 mb-1">Dedicated frontend interface</p>
            <p className="text-sm text-gray-400">Third party accesses a simple and secure web page displaying order summary and amount to pay</p>
          </div>
          <div className="bg-pink-900/40 p-4 rounded-xl border border-pink-500/30">
            <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center text-white font-bold mb-3">5</div>
            <p className="font-bold text-pink-300 mb-1">Payment without registration</p>
            <p className="text-sm text-gray-400">Third party makes payment directly via platform (credit card) without creating an account</p>
          </div>
          <div className="bg-green-900/40 p-4 rounded-xl border border-green-500/30">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mb-3">6</div>
            <p className="font-bold text-green-300 mb-1">Instant confirmation</p>
            <p className="text-sm text-gray-400">Parent and third party receive immediate confirmation with payment receipt</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardHeader><CardTitle className="text-lg flex items-center space-x-2"><Star className="w-5 h-5 text-blue-600" /><span>Benefits for Parent</span></CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-3"><Users className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Total control over order and food choices</p></div>
          <div className="flex items-start space-x-3"><BarChart3 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Financial flexibility — easily delegate payment</p></div>
          <div className="flex items-start space-x-3"><Eye className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Transparency — third party sees exactly what they pay</p></div>
          <div className="flex items-start space-x-3"><Clock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Complete history of who paid what and when</p></div>
        </CardContent>
      </Card>
      <Card className="border-none shadow-lg bg-gradient-to-br from-orange-50 to-amber-50">
        <CardHeader><CardTitle className="text-lg flex items-center space-x-2"><Award className="w-5 h-5 text-orange-600" /><span>Benefits for Third Party</span></CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-3"><Zap className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Ultra simple — 1 click, payment, done</p></div>
          <div className="flex items-start space-x-3"><FileText className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">No registration required to pay</p></div>
          <div className="flex items-start space-x-3"><Shield className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Secure — payment via certified platform</p></div>
          <div className="flex items-start space-x-3"><Mail className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Instant email receipt for proof</p></div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-xl bg-gradient-to-br from-slate-800 to-blue-900 text-white">
      <CardHeader><CardTitle className="text-xl text-white flex items-center space-x-2"><Star className="w-5 h-5 text-yellow-400" /><span>Typical Use Cases</span></CardTitle></CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white/10 p-5 rounded-xl text-center"><Users className="w-10 h-10 mx-auto mb-3 text-cyan-400" /><p className="font-bold text-lg mb-1">Grandparents</p><p className="text-sm text-gray-300">Contribute to their grandchildren's meals</p></div>
          <div className="bg-white/10 p-5 rounded-xl text-center"><Gift className="w-10 h-10 mx-auto mb-3 text-pink-400" /><p className="font-bold text-lg mb-1">Birthday Gift</p><p className="text-sm text-gray-300">Offer school meals as a useful gift</p></div>
          <div className="bg-white/10 p-5 rounded-xl text-center"><Heart className="w-10 h-10 mx-auto mb-3 text-red-400" /><p className="font-bold text-lg mb-1">Extended Family</p><p className="text-sm text-gray-300">Uncles, aunts, godparents who want to help</p></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-xl">
      <CardHeader><CardTitle className="text-xl">Usage Scenarios</CardTitle></CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200"><Scale className="w-8 h-8 text-blue-600 mb-2" /><h4 className="font-bold text-blue-700 mb-2">Shared Custody</h4><ul className="text-sm text-gray-700 space-y-1"><li>- Week A/B</li><li>- Alternating weekends</li><li>- School holidays</li></ul></div>
          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 p-5 rounded-xl border border-cyan-200"><Users className="w-8 h-8 text-cyan-600 mb-2" /><h4 className="font-bold text-cyan-700 mb-2">Involved Grandparents</h4><ul className="text-sm text-gray-700 space-y-1"><li>- Read-only access</li><li>- Child monitoring</li><li>- Optional financial participation</li></ul></div>
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-5 rounded-xl border border-pink-200"><Heart className="w-8 h-8 text-pink-600 mb-2" /><h4 className="font-bold text-pink-700 mb-2">Separated Parents</h4><ul className="text-sm text-gray-700 space-y-1"><li>- Total transparency</li><li>- Avoids financial conflicts</li><li>- Fluid communication</li></ul></div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-5 rounded-xl border border-purple-200"><Shield className="w-8 h-8 text-purple-600 mb-2" /><h4 className="font-bold text-purple-700 mb-2">Multiple Legal Guardians</h4><ul className="text-sm text-gray-700 space-y-1"><li>- Configurable rights</li><li>- Multi-actor management</li><li>- Legal compliance</li></ul></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-br from-gray-50 to-slate-100">
      <CardHeader><CardTitle className="text-xl">Key Benefits</CardTitle></CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start space-x-3 p-3"><Eye className="w-6 h-6 text-blue-500 flex-shrink-0" /><div><p className="font-bold text-gray-800 text-sm">Total Transparency</p><p className="text-xs text-gray-500">All actions visible — Complete shared history</p></div></div>
          <div className="flex items-start space-x-3 p-3"><RefreshCw className="w-6 h-6 text-green-500 flex-shrink-0" /><div><p className="font-bold text-gray-800 text-sm">Avoids Duplicates</p><p className="text-xs text-gray-500">Synchronized orders — Anti-duplicate alerts</p></div></div>
          <div className="flex items-start space-x-3 p-3"><MessageSquare className="w-6 h-6 text-cyan-500 flex-shrink-0" /><div><p className="font-bold text-gray-800 text-sm">Fluid Communication</p><p className="text-xs text-gray-500">Contextual notifications — 1-click validation</p></div></div>
          <div className="flex items-start space-x-3 p-3"><CreditCard className="w-6 h-6 text-orange-500 flex-shrink-0" /><div><p className="font-bold text-gray-800 text-sm">Clear Financial Management</p><p className="text-xs text-gray-500">Automatic split — Separate invoices — Detailed reports</p></div></div>
          <div className="flex items-start space-x-3 p-3"><Lock className="w-6 h-6 text-red-500 flex-shrink-0" /><div><p className="font-bold text-gray-800 text-sm">Guaranteed Privacy</p><p className="text-xs text-gray-500">Encrypted banking data — Granular permissions</p></div></div>
          <div className="flex items-start space-x-3 p-3"><RefreshCw className="w-6 h-6 text-purple-500 flex-shrink-0" /><div><p className="font-bold text-gray-800 text-sm">Real-Time Sync</p><p className="text-xs text-gray-500">Instant updates — 0 conflicts</p></div></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader><CardTitle className="text-xl">How It Works</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start space-x-4"><div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div><div className="border-l-2 border-blue-200 pl-4 pb-4"><p className="text-gray-700">Primary parent <strong className="text-blue-600">configures and invites</strong> the other parent</p></div></div>
        <div className="flex items-start space-x-4"><div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div><div className="border-l-2 border-blue-200 pl-4 pb-4"><p className="text-gray-700">Invited parent <strong className="text-blue-600">accepts and creates account</strong></p></div></div>
        <div className="flex items-start space-x-4"><div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div><div className="border-l-2 border-blue-200 pl-4 pb-4"><p className="text-gray-700">Both parents <strong className="text-blue-600">validate payment split</strong></p></div></div>
        <div className="flex items-start space-x-4"><div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</div><div className="pl-4"><p className="text-gray-700">Future transactions are <strong className="text-green-600">automatically split</strong></p></div></div>
      </CardContent>
    </Card>
  </div>
);


export const SchoolSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img src="https://images.pexels.com/photos/1001914/pexels-photo-1001914.jpeg" alt="School" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/90 to-blue-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">School Portal</h2>
          <p className="text-xl">Management dashboard</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2"><School className="w-6 h-6 text-indigo-600" /><span>School Management Cockpit</span></CardTitle>
        <CardDescription>Management tool for food satisfaction and daily operations</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-lg">
            <BarChart3 className="w-6 h-6 text-indigo-600 mb-2" />
            <h4 className="font-bold text-indigo-600 mb-2">Daily View</h4>
            <ul className="text-sm text-gray-700 space-y-1"><li>- Ordered meals</li><li>- Absence management</li><li>- Real-time adjustments</li></ul>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-lg">
            <Star className="w-6 h-6 text-yellow-600 mb-2" />
            <h4 className="font-bold text-yellow-600 mb-2">Satisfaction</h4>
            <ul className="text-sm text-gray-700 space-y-1"><li>- 30-day average score</li><li>- Evolution trends</li><li>- Analysis by dish</li></ul>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg">
            <Bell className="w-6 h-6 text-orange-600 mb-2" />
            <h4 className="font-bold text-orange-600 mb-2">Alerts</h4>
            <ul className="text-sm text-gray-700 space-y-1"><li>- Dishes below threshold</li><li>- Delivery delays</li><li>- Automatic</li></ul>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
            <MessageSquare className="w-6 h-6 text-blue-600 mb-2" />
            <h4 className="font-bold text-blue-600 mb-2">Communication</h4>
            <ul className="text-sm text-gray-700 space-y-1"><li>- Direct caterer channel</li><li>- School direction</li><li>- Integrated chat</li></ul>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
            <TrendingUp className="w-6 h-6 text-green-600 mb-2" />
            <h4 className="font-bold text-green-600 mb-2">Reports</h4>
            <ul className="text-sm text-gray-700 space-y-1"><li>- Automatable</li><li>- Customizable</li><li>- Flexible format</li><li>- Adjustable frequency</li></ul>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-lg">
            <Download className="w-6 h-6 text-purple-600 mb-2" />
            <h4 className="font-bold text-purple-600 mb-2">Exports</h4>
            <ul className="text-sm text-gray-700 space-y-1"><li>- Excel / PDF</li><li>- Class lists</li><li>- Custom reports</li></ul>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-slate-50 to-gray-100 p-5 rounded-xl border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center space-x-2"><Settings className="w-5 h-5 text-gray-600" /><span>Settings</span></h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Configurable satisfaction threshold</p><p className="text-xs text-gray-500">E.g.: alert if &lt; 50%</p></div></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Delay before alert</p><p className="text-xs text-gray-500">E.g.: after 3 consecutive meals</p></div></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Automatic reports</p><p className="text-xs text-gray-500">Flexible scheduling</p></div></div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-50 to-gray-100 p-5 rounded-xl border border-gray-200">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center space-x-2"><Shield className="w-5 h-5 text-gray-600" /><span>Permissions</span></h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Access limited to the assigned school</p></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Multiple roles</p><p className="text-xs text-gray-500">Principal, Secretary, Manager</p></div></div>
              <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">No access to other schools' data</p></div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-indigo-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-xl">Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <strong>Configurable satisfaction threshold</strong>
              <p className="text-sm text-gray-600">Ex: alert if &lt; 50%</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <strong>Delay before alert</strong>
              <p className="text-sm text-gray-600">Ex: after 3 consecutive meals</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <strong>Automatic reports</strong>
              <p className="text-sm text-gray-600">Flexible scheduling</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl flex items-center space-x-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            <span>Permissions</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5" />
            <p className="text-gray-700">Access limited to the <strong>concerned establishment</strong></p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <strong>Multiple roles</strong>
              <p className="text-sm text-gray-600">Director, Secretary, Manager</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5" />
            <p className="text-gray-700">No access to <strong>other schools' data</strong></p>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

export const RegionSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img src="https://images.pexels.com/photos/6981024/pexels-photo-6981024.jpeg" alt="Region" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-teal-600/90 to-green-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">School Direction</h2>
          <p className="text-xl">School portfolio management</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2"><Building2 className="w-6 h-6 text-teal-600" /><span>Territorial Coordination</span></CardTitle>
        <CardDescription>Supervision and coordination between field, caterers and SSC</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><BarChart3 className="w-4 h-4 inline text-teal-400" /> Consolidated View</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Aggregated volumes per school</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Portfolio average satisfaction</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Real-time active alerts</span>
              </li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><TrendingUp className="w-4 h-4 inline text-teal-400" /> Comparative Analysis</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Caterer / school performance</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Portfolio benchmarking</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Trend identification</span>
              </li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><ClipboardList className="w-4 h-4 inline text-teal-400" /> Portfolio Reports</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Automated PDF/Excel export</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Customizable reports</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Flexible scheduling</span>
              </li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-green-50 p-4 rounded-lg">
            <h4 className="font-bold text-teal-600 mb-3"><MessageSquare className="w-4 h-4 inline text-cyan-400" /> Communication</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Schools ↔ caterers channel</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Link with SSC</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full"></span>
                <span>Multi-stakeholder chat</span>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Journalisation, Gouvernance, Communication EN */}
    <div className="grid md:grid-cols-3 gap-4">
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center space-x-2"><Shield className="w-5 h-5 text-orange-500" /><span>Logging & Audit</span></CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">All actions are traceable</p></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Complete audit</p><p className="text-xs text-gray-500">Read, modify, communication</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Compliance and transparency guaranteed</p></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Complete decision history</p></div>
        </CardContent>
      </Card>
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center space-x-2"><Bell className="w-5 h-5 text-pink-500" /><span>Governance & Alerts</span></CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Strategic indicators</p><p className="text-xs text-gray-500">Real-time dashboards</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Automatic critical alerts</p><p className="text-xs text-gray-500">Immediate notifications</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Configurable rules</p><p className="text-xs text-gray-500">Thresholds, frequencies, priorities</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Automatic escalation if needed</p></div>
        </CardContent>
      </Card>
      <Card className="border-none shadow-lg">
        <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center space-x-2"><MessageSquare className="w-5 h-5 text-pink-500" /><span>Communication</span></CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Food policies</p><p className="text-xs text-gray-500">Dissemination and implementation</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Operational directives</p><p className="text-xs text-gray-500">Official communications</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-semibold text-gray-700">Standards & norms</p><p className="text-xs text-gray-500">Regulatory compliance</p></div></div>
          <div className="flex items-start space-x-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><p className="text-sm text-gray-700">Integrated multi-actor channel</p></div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-xl bg-gradient-to-r from-teal-50 to-cyan-50">
      <CardHeader>
        <CardTitle className="text-2xl">Usage Logic</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <div className="flex-1">
              <p className="text-gray-700">Each school direction manages <strong className="text-teal-600">its school or a portfolio of establishments</strong></p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <div className="flex-1">
              <p className="text-gray-700">Can <strong className="text-teal-600">intervene before</strong> the SSC has to</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <div className="flex-1">
              <p className="text-gray-700">Alerts can be automatically <strong className="text-teal-600">escalated to SSC via internal channel</strong></p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">4</div>
            <div className="flex-1">
              <p className="text-gray-700">Portfolio settings: <strong className="text-teal-600">thresholds, frequencies, priorities</strong></p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const SSCSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c" alt="SSC" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-red-600/90 to-rose-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">School Board Portal</h2>
          <p className="text-xl">Global food governance</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2"><Landmark className="w-6 h-6 text-red-600" /><span>Governance Center</span></CardTitle>
        <CardDescription>Complete centralization of school food governance</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg"><h4 className="font-bold text-red-600 mb-2"><Network className="w-4 h-4 inline text-red-400" /> Global View</h4><ul className="text-sm text-gray-700 space-y-1"><li>• All school portfolios</li><li>• All schools</li><li>• All caterers</li></ul></div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg"><h4 className="font-bold text-red-600 mb-2"><BarChart3 className="w-4 h-4 inline text-red-400" /> Benchmarking</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Performance</li><li>• Quality</li><li>• Compliance</li></ul></div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg"><h4 className="font-bold text-red-600 mb-2"><TrendingUp className="w-4 h-4 inline text-red-400" /> Reports</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Monthly / quarterly / annual</li><li>• Customizable</li><li>• Automation</li></ul></div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg"><h4 className="font-bold text-red-600 mb-2"><Target className="w-4 h-4 inline text-red-400" /> Governance</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Strategic indicators</li><li>• Critical alerts</li><li>• Dashboards</li></ul></div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg"><h4 className="font-bold text-red-600 mb-2"><MessageSquare className="w-4 h-4 inline text-red-400" /> Communication</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Policies</li><li>• Directives</li><li>• Standards</li></ul></div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg"><h4 className="font-bold text-red-600 mb-2"><TrendingUp className="w-4 h-4 inline text-red-400" /> Sustainability</h4><ul className="text-sm text-gray-700 space-y-1"><li>• Healthy menus</li><li>• Waste reduction</li><li>• Local products</li></ul></div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-red-50 to-rose-50">
        <CardHeader>
          <CardTitle className="text-xl">Flexible Governance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Total control</strong>
              <p className="text-sm text-gray-600">All permissions and access</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Direct communication</strong>
              <p className="text-sm text-gray-600">With caterers or via school directions</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Flexible delegation</strong>
              <p className="text-sm text-gray-600">To school directions as needed</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Configurable rules</strong>
              <p className="text-sm text-gray-600">Escalation, alerts and reports</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl flex items-center space-x-2">
            <Shield className="w-5 h-5 text-red-600" />
            <span>Audit Logging</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-gray-700">All actions are <strong>traceable</strong></p>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <strong>Complete audit</strong>
              <p className="text-sm text-gray-600">Reading, modification, communication</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-gray-700">Compliance and transparency guaranteed</p>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

export const EvaluationSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img src="https://images.unsplash.com/photo-1514575110897-1253ff7b2ccb" alt="Evaluation" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/90 to-orange-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <h2 className="text-4xl font-bold mb-4">Evaluation System</h2>
          <p className="text-xl">Measuring real satisfaction</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2"><Star className="w-6 h-6 text-yellow-600" /><span>Universal 3-Level Rating</span></CardTitle>
        <CardDescription>Simple, intuitive and effective for all ages</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl text-center border-2 border-green-200"><div className="text-6xl mb-3"><Star className="w-12 h-12 text-green-400" /></div><div className="font-bold text-2xl text-green-600 mb-2">+1</div><div className="text-green-700 font-semibold">Liked</div><div className="mt-2 px-3 py-1 bg-green-500 text-white rounded-full text-sm inline-block">Green</div></div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-6 rounded-xl text-center border-2 border-yellow-200"><div className="text-6xl mb-3"><AlertTriangle className="w-12 h-12 text-yellow-400" /></div><div className="font-bold text-2xl text-yellow-600 mb-2">0</div><div className="text-yellow-700 font-semibold">Neutral</div><div className="mt-2 px-3 py-1 bg-yellow-500 text-white rounded-full text-sm inline-block">Yellow</div></div>
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-6 rounded-xl text-center border-2 border-red-200"><div className="text-6xl mb-3"><X className="w-12 h-12 text-red-400" /></div><div className="font-bold text-2xl text-red-600 mb-2">-1</div><div className="text-red-700 font-semibold">Disliked</div><div className="mt-2 px-3 py-1 bg-red-500 text-white rounded-full text-sm inline-block">Red</div></div>
        </div>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardHeader>
          <CardTitle className="text-xl">Intelligent Weighting</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <strong>30-day rolling window</strong>
              <p className="text-sm text-gray-600">Evaluation over recent period</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <strong>Recent meals prioritized</strong>
              <p className="text-sm text-gray-600">Count more in calculation</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <strong>Automatic calculations</strong>
              <p className="text-sm text-gray-600">Averages and trends in real-time</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl">Evolution and Alerts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start space-x-2">
            <Bell className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <strong>Arrows and % evolution</strong>
              <p className="text-sm text-gray-600">Displayed in school and caterer portals</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Bell className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <strong>Automatic alert</strong>
              <p className="text-sm text-gray-600">If score &lt; configured threshold</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <TrendingUp className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <strong>Trend analysis</strong>
              <p className="text-sm text-gray-600">Pattern identification</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-r from-purple-50 to-pink-50">
      <CardHeader>
        <CardTitle className="text-xl">Playful Interaction</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-bold text-purple-600 mb-3"><Layout className="w-4 h-4 inline text-purple-400" /> Child Mode</h4>
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                <span className="text-gray-700">Animated and interactive game</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                <span className="text-gray-700">Badge system / Playful rewards</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                <span className="text-gray-700">100% customizable avatar</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-pink-600 mb-3"><Users className="w-4 h-4 inline text-cyan-400" /> Parent Mode</h4>
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-pink-600 rounded-full"></span>
                <span className="text-gray-700">Simple static smiley</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-pink-600 rounded-full"></span>
                <span className="text-gray-700">Streamlined interface</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-pink-600 rounded-full"></span>
                <span className="text-gray-700">Quick evaluation</span>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const AlertsSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl bg-gradient-to-r from-rose-600/90 to-pink-600/90 flex items-center justify-center">
      <div className="text-center text-white p-6"><Bell className="w-20 h-20 mx-auto mb-4" /><h2 className="text-4xl font-bold mb-4">Alerts & Reports</h2><p className="text-xl">Intelligent notification system</p></div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader><CardTitle className="text-2xl flex items-center space-x-2"><Bell className="w-6 h-6 text-rose-600" /><span>Alert Types</span></CardTitle></CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-500"><h4 className="font-bold text-red-600 mb-2 flex items-center space-x-2"><span className="w-3 h-3 bg-red-500 rounded-full inline-block"></span><span>Quality</span></h4><p className="text-sm text-gray-700">Dishes below configured satisfaction threshold</p></div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-500"><h4 className="font-bold text-orange-600 mb-2 flex items-center space-x-2"><span className="w-3 h-3 bg-orange-500 rounded-full inline-block"></span><span>Logistics</span></h4><p className="text-sm text-gray-700">Delivery delays, supply issues</p></div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-500"><h4 className="font-bold text-blue-600 mb-2 flex items-center space-x-2"><span className="w-3 h-3 bg-blue-500 rounded-full inline-block"></span><span>Participation</span></h4><p className="text-sm text-gray-700">Significant drop in orders</p></div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-lg border-l-4 border-yellow-500"><h4 className="font-bold text-yellow-600 mb-2 flex items-center space-x-2"><span className="w-3 h-3 bg-yellow-500 rounded-full inline-block"></span><span>Negative Trend</span></h4><p className="text-sm text-gray-700">Progressive satisfaction deterioration</p></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-rose-50 to-pink-50">
      <CardHeader>
        <CardTitle className="text-xl">Hierarchical Escalation</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <div className="flex-1">
              <div className="font-bold text-gray-800">School</div>
              <p className="text-sm text-gray-600">First intervention and local management</p>
            </div>
          </div>
          <div className="ml-6 w-1 h-8 bg-gray-300"></div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <div className="flex-1">
              <div className="font-bold text-gray-800">School Direction</div>
              <p className="text-sm text-gray-600">If not resolved after configured delay</p>
            </div>
          </div>
          <div className="ml-6 w-1 h-8 bg-gray-300"></div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <div className="flex-1">
              <div className="font-bold text-gray-800">SSC</div>
              <p className="text-sm text-gray-600">Final escalation for strategic decision</p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-white rounded-lg border-2 border-rose-200">
            <p className="text-center text-rose-600 font-medium">Each level can intervene, annotate or close an alert</p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl">Customizable Reports</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg">
            <h4 className="font-bold text-blue-600 mb-3"><FileText className="w-4 h-4 inline text-blue-400" /> Content</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Top / Flop Dishes</li>
              <li>• Satisfaction evolution</li>
              <li>• Participation</li>
              <li>• Active alerts</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
            <h4 className="font-bold text-purple-600 mb-3"><Clock className="w-4 h-4 inline text-purple-400" /> Frequency</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Daily</li>
              <li>• Weekly</li>
              <li>• Monthly</li>
              <li>• On demand</li>
            </ul>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
            <h4 className="font-bold text-green-600 mb-3"><BarChart3 className="w-4 h-4 inline text-green-400" /> Format</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Detailed PDF</li>
              <li>• Excel report</li>
              <li>• Interactive charts</li>
              <li>• Automatic email</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const SecuritySectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl bg-gradient-to-r from-gray-700/90 to-slate-700/90 flex items-center justify-center">
      <div className="text-center text-white p-6"><Shield className="w-20 h-20 mx-auto mb-4" /><h2 className="text-4xl font-bold mb-4">Security & Privacy</h2><p className="text-xl">Absolute data protection</p></div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader><CardTitle className="text-2xl flex items-center space-x-2"><Shield className="w-6 h-6 text-gray-700" /><span>Secured Architecture</span></CardTitle></CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-lg border-2 border-gray-200"><h4 className="font-bold text-gray-700 mb-4 flex items-center space-x-2"><Shield className="w-5 h-5" /><span>Isolated Multi-tenant</span></h4><ul className="space-y-3"><li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" /><span className="text-gray-700">Each entity = protected space</span></li><li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" /><span className="text-gray-700">Complete data isolation</span></li></ul></div>
          <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-lg border-2 border-gray-200"><h4 className="font-bold text-gray-700 mb-4 flex items-center space-x-2"><Shield className="w-5 h-5" /><span>Filtered Data</span></h4><ul className="space-y-3"><li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" /><span className="text-gray-700">Via secured central back-end</span></li><li className="flex items-start space-x-2"><CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" /><span className="text-gray-700">"Need to know" principle</span></li></ul></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardHeader>
        <CardTitle className="text-xl">Permissions (RBAC)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg">
            <h4 className="font-bold text-blue-600 mb-2">Role-Based Access Control</h4>
            <p className="text-gray-700">Precise roles and rights for each user according to their function</p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">P</div>
                <span>Parent</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Their children only</li>
                <li>• Their orders</li>
                <li>• Their payments</li>
                <li>• Personal history</li>
                <li>• Children's ratings</li>
                <li>• Personal notifications</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">C</div>
                <span>Caterer</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Their orders</li>
                <li>• Their client schools</li>
                <li>• Menu management</li>
                <li>• Sales statistics</li>
                <li>• Payments received</li>
                <li>• Operational alerts</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">S</div>
                <span>School</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Their establishment</li>
                <li>• Their statistics</li>
                <li>• Their alerts</li>
                <li>• Menu management</li>
                <li>• Local reports</li>
                <li>• Caterer coordination</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">D</div>
                <span>School Direction</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Their school portfolio</li>
                <li>• Their schools</li>
                <li>• Consolidated reports</li>
                <li>• Escalated alerts</li>
                <li>• Multi-school / multi-establishment supervision</li>
                <li>• Comparative analysis</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="font-semibold text-gray-800 mb-3 flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">S</div>
                <span>SSC</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Global view</li>
                <li>• All data</li>
                <li>• Total control</li>
                <li>• Policy management</li>
                <li>• Strategic reports</li>
                <li>• System administration</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-gray-50 to-slate-50">
      <CardHeader>
        <CardTitle className="text-xl">Data Security</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white"><Lock className="w-4 h-4" /></div>
            <div>
              <div className="font-bold text-gray-800">Complete encryption</div>
              <p className="text-sm text-gray-600">In transit (TLS/SSL) and at rest (AES-256)</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white"><FileText className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">Complete audit log</div>
              <p className="text-sm text-gray-600">Traceability of all actions and access</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white"><Download className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">Automatic backups</div>
              <p className="text-sm text-gray-600">Daily backup with configurable retention</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white"><Shield className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-gray-800">DDoS protection and WAF</div>
              <p className="text-sm text-gray-600">Web application firewall and attack protection</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const FlowSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl bg-gradient-to-r from-cyan-600/90 to-blue-600/90 flex items-center justify-center">
      <div className="text-center text-white p-6"><Network className="w-20 h-20 mx-auto mb-4" /><h2 className="text-4xl font-bold mb-4">Information Flow</h2><p className="text-xl">Orchestrated communication</p></div>
    </div>

    <Card className="border-none shadow-xl">
      <CardHeader><CardTitle className="text-2xl flex items-center space-x-2"><Network className="w-6 h-6 text-cyan-600" /><span>Flow Architecture</span></CardTitle></CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-lg border-2 border-blue-200"><h4 className="font-bold text-blue-600 mb-4 text-lg">Upward Vertical Flows</h4><div className="space-y-3"><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">P</div><span className="text-gray-700">Parents</span></div><div className="ml-4 w-1 h-8 bg-gray-300"></div><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">S</div><span className="text-gray-700">Schools</span></div><div className="ml-4 w-1 h-8 bg-gray-300"></div><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">D</div><span className="text-gray-700">School Directions</span></div><div className="ml-4 w-1 h-8 bg-gray-300"></div><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">S</div><span className="text-gray-700">SSC</span></div></div><div className="mt-4 p-3 bg-blue-100 rounded text-sm text-blue-700"><strong>Orders, feedback, alerts</strong> flow up the hierarchy</div></div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200"><h4 className="font-bold text-green-600 mb-4 text-lg">Downward Vertical Flows</h4><div className="space-y-3"><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">S</div><span className="text-gray-700">SSC</span></div><div className="ml-4 w-1 h-8 bg-gray-300"></div><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold">D</div><span className="text-gray-700">School Directions</span></div><div className="ml-4 w-1 h-8 bg-gray-300"></div><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold">S</div><span className="text-gray-700">Schools</span></div><div className="ml-4 w-1 h-8 bg-gray-300"></div><div className="flex items-center space-x-2"><div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">C</div><span className="text-gray-700">Caterers</span></div></div><div className="mt-4 p-3 bg-green-100 rounded text-sm text-green-700"><strong>Policies, directives, standards</strong> flow down the hierarchy</div></div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-purple-50 to-pink-50">
      <CardHeader>
        <CardTitle className="text-xl">Horizontal Flows</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg border-2 border-purple-200">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">S</div>
                <span className="font-semibold">Schools</span>
              </div>
              <span className="text-2xl text-purple-600">↔</span>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">C</div>
                <span className="font-semibold">Caterers</span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-3">Direct communication for operational management</p>
          </div>

          <div className="bg-white p-4 rounded-lg border-2 border-pink-200">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">C</div>
                <span className="font-semibold">Caterers</span>
              </div>
              <span className="text-2xl text-pink-600">↔</span>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">P</div>
                <span className="font-semibold">Parents</span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-3">Orders, confirmations, feedback</p>
          </div>

          <div className="mt-4 p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg">
            <p className="text-center font-medium text-gray-700">
              Horizontal flows enable <strong className="text-purple-600">immediate responsiveness</strong> without waiting for hierarchical escalation
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl">Central Back-end Role</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-lg">
            <h4 className="font-bold text-cyan-600 mb-3"><Target className="w-4 h-4 inline text-cyan-400" /> Orchestration</h4>
            <p className="text-sm text-gray-700">Coordinates all information flows in real-time</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg">
            <h4 className="font-bold text-blue-600 mb-3"><Lock className="w-4 h-4 inline text-blue-400" /> Security</h4>
            <p className="text-sm text-gray-700">Applies access rules and permissions</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg">
            <h4 className="font-bold text-indigo-600 mb-3"><PieChart className="w-4 h-4 inline text-indigo-400" /> Aggregation</h4>
            <p className="text-sm text-gray-700">Generates statistics and consolidated reports</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);
